import { useEffect } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { getAuthMode } from "@/lib/api";
import { unscopedKey } from "@/lib/queryClient";
import { AuthProductShell } from "@/features/auth/components/AuthProductShell";
import { KratosRecoveryPanel } from "@/features/auth/kratos/KratosRecoveryPanel";

/**
 * `/recovery` — Kratos account recovery (email → code → new password),
 * fully in-app. Legacy/dual installs keep `/reset-password`; this
 * route bounces to `/login` when Kratos mode is off.
 */
export function RecoveryPage() {
  const navigate = useNavigate();
  const search = useSearch({ from: "/recovery" });
  const authMode = useQuery({
    queryKey: unscopedKey("auth-mode"),
    queryFn: getAuthMode,
    staleTime: 60_000,
  });
  const kratosEnabled = authMode.data?.kratos_enabled ?? false;

  useEffect(() => {
    if (authMode.data && !authMode.data.kratos_enabled) {
      void navigate({ to: "/login", replace: true });
    }
  }, [authMode.data, navigate]);

  return (
    <AuthProductShell
      testId="recovery-page"
      title="Recover your account"
      description="We'll email a one-time code, then you choose a new password — all without leaving Tktic."
    >
      {authMode.isPending ? (
        <p className="font-mono text-xs text-ink-muted">
          Checking sign-in options...
        </p>
      ) : kratosEnabled ? (
        <KratosRecoveryPanel flowId={search.flow} />
      ) : null}
    </AuthProductShell>
  );
}
